// src/app/core/services/impression-tracking.service.ts
import { Injectable, OnDestroy, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { ApiService } from './api/api.service';

export type EngagementLevel = 'impression' | 'engaged';
export type EntityType = 'offer' | 'ad';
export type VideoEngagementTrigger = 'halfway' | 'ended';

export interface ImpressionEvent {
  id: string;
  entityType: EntityType;
  level: EngagementLevel;
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class ImpressionTrackingService implements OnDestroy {
  private http = inject(HttpClient);
  private apiService = inject(ApiService);

  private observer!: IntersectionObserver;
  private trackedIds = new Set<string>();
  private debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private videoListeners = new Map<string, {
    element: HTMLVideoElement;
    handler: () => void;
    event: 'timeupdate' | 'ended';
  }>();

  private readonly API_URL = this.apiService.getApiUrl('/shopasst/api/customer/offers/engagement/view');
  private readonly VISIBILITY_THRESHOLD = 0.75; // 75% visible
  private readonly VIEW_TIME_MS = 1000; // 1 second

  constructor() {
    this.initObserver();
  }

  // private initObserver(): void {
  //   this.observer = new IntersectionObserver(
  //     (entries) => this.handleEntries(entries),
  //     {
  //       threshold: this.buildThresholds(),
  //       rootMargin: '0px 0px 0px 0px'
  //     }
  //   );
  // }

  private initObserver(): void {
    this.observer = new IntersectionObserver(
      (entries) => this.handleEntries(entries),
      {
        threshold: this.VISIBILITY_THRESHOLD,
        rootMargin: '0px'
      }
    );
  }

  // private buildThresholds(): number[] {
  //   return Array.from({ length: 101 }, (_, i) => i / 100);
  // }

  // ── Standard observe 

  observe(element: Element, id: string, entityType: EntityType): void {
    element.setAttribute('data-impression-id', id);
    element.setAttribute('data-impression-type', entityType);
    this.observer.observe(element);
  }

  unobserve(element: Element, id: string): void {
    this.observer.unobserve(element);
    this.clearDebounce(id);
  }

  // ── Video observe 
  observeVideo(
    videoEl: HTMLVideoElement,
    id: string,
    entityType: EntityType,
    trigger: VideoEngagementTrigger = 'halfway'
  ): void {
    this.unobserveVideo(id);

    videoEl.muted = true;
    videoEl.volume = 0;

    if (trigger === 'ended') {
      const handler = () => {
        this.fireImpression({ id, entityType, level: 'engaged', timestamp: Date.now() });
        videoEl.removeEventListener('ended', handler);
        this.videoListeners.delete(id);
      };

      videoEl.addEventListener('ended', handler);
      this.videoListeners.set(id, { element: videoEl, handler, event: 'ended' });
      return;
    }

    const handler = () => {
      if (!videoEl.duration || videoEl.duration === Infinity) return;
      if (videoEl.currentTime >= videoEl.duration / 2) {
        this.fireImpression({ id, entityType, level: 'engaged', timestamp: Date.now() });
        videoEl.removeEventListener('timeupdate', handler);
        this.videoListeners.delete(id);
      }
    };

    videoEl.addEventListener('timeupdate', handler);
    this.videoListeners.set(id, { element: videoEl, handler, event: 'timeupdate' });
  }

  unobserveVideo(id: string): void {
    const entry = this.videoListeners.get(id);
    if (entry) {
      entry.element.removeEventListener(entry.event, entry.handler);
      this.videoListeners.delete(id);
    }
  }

  
  // private isFullyVisible(el: Element): boolean {
  //   const rect = el.getBoundingClientRect();
  //   const vw = window.innerWidth  || document.documentElement.clientWidth;
  //   const vh = window.innerHeight || document.documentElement.clientHeight;
  //   const EPSILON = 1; // px tolerance for sub-pixel rendering differences

  //   return (
  //     rect.top    >= -EPSILON     &&
  //     rect.left   >= -EPSILON     &&
  //     rect.bottom <= vh + EPSILON &&
  //     rect.right  <= vw + EPSILON &&
  //     rect.width  > 0             &&
  //     rect.height > 0
  //   );
  // }

  // ── Intersection handler  

  // private handleEntries(entries: IntersectionObserverEntry[]): void {
  //   for (const entry of entries) {
  //     const id         = entry.target.getAttribute('data-impression-id');
  //     const entityType = entry.target.getAttribute('data-impression-type') as EntityType;

  //     if (!id || !entityType) continue;

  //     // Element is not intersecting at all — kill any pending timer
  //     if (!entry.isIntersecting) {
  //       this.clearDebounce(id);
  //       continue;
  //     }

  //     // Use getBoundingClientRect as the strict "fully visible" gate
  //     // (threshold: 1.0 alone fails for elements clipped by horizontal scroll)
  //     const fullyVisible = this.isFullyVisible(entry.target);

  //     if (!fullyVisible) {
  //       // Card is partially in view but not 100% — clear timer if it was started
  //       this.clearDebounce(id);
  //       continue;
  //     }

  //     // Card is 100% visible — start debounce if not already running
  //     if (!this.debounceTimers.has(id)) {
  //       const timer = setTimeout(() => {
  //         this.debounceTimers.delete(id);

  //         // Re-check at fire time — user may have scrolled away during the 1s
  //         if (this.isFullyVisible(entry.target)) {
  //           this.fireImpression({
  //             id,
  //             entityType,
  //             level: 'engaged',
  //             timestamp: Date.now()
  //           });
  //         }
  //       }, 1000);

  //       this.debounceTimers.set(id, timer);
  //     }
  //   }
  // }

  private handleEntries(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      const id = entry.target.getAttribute('data-impression-id');
      const entityType = entry.target.getAttribute('data-impression-type') as EntityType;

      if (!id || !entityType) continue;

      // Less than 75% visible -> cancel pending timer
      if (!entry.isIntersecting || entry.intersectionRatio < this.VISIBILITY_THRESHOLD) {
        this.clearDebounce(id);
        continue;
      }

      // Already waiting? Do nothing.
      if (this.debounceTimers.has(id)) {
        continue;
      }

      const timer = setTimeout(() => {
        this.debounceTimers.delete(id);

        // Re-check before firing
        const rect = (entry.target as Element).getBoundingClientRect();
        const visibleHeight =
          Math.min(rect.bottom, window.innerHeight) -
          Math.max(rect.top, 0);

        const visibleWidth =
          Math.min(rect.right, window.innerWidth) -
          Math.max(rect.left, 0);

        const visibleArea =
          Math.max(0, visibleHeight) *
          Math.max(0, visibleWidth);

        const totalArea = rect.width * rect.height;

        const ratio = totalArea > 0 ? visibleArea / totalArea : 0;

        if (ratio >= this.VISIBILITY_THRESHOLD) {
          this.fireImpression({
            id,
            entityType,
            level: 'engaged',
            timestamp: Date.now()
          });
        }
      }, this.VIEW_TIME_MS);

      this.debounceTimers.set(id, timer);
    }
  }

  // ── Fire  

  private fireImpression(event: ImpressionEvent): void {
    const dedupKey = `${event.entityType}-${event.id}`;
    if (this.trackedIds.has(dedupKey)) return;
    this.trackedIds.add(dedupKey);

    console.log(`[Impression] entityId=${event.id} entityType=${event.entityType} level=${event.level}`);

    this.http.post(this.API_URL, {
      entityId:   event.id,
      entityType: event.entityType,
    }).pipe(
      catchError((err) => {
        if (err.status === 400) {
          console.log(`[Impression] Already tracked today: ${event.entityType} ${event.id}`);
        } else {
          this.trackedIds.delete(dedupKey);
          console.error(`[Impression] API error:`, err.status, err.error?.message);
        }
        return of(null);
      })
    ).subscribe((res: any) => {
      if (res?.success) {
        console.log(`[Impression] ✓ Recorded — points: ${res.data?.pointsEarned}, score: ${res.data?.totalEngagementScore}`);
      }
    });
  }

  // ── Helpers 

  private clearDebounce(id: string): void {
    const timer = this.debounceTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.debounceTimers.delete(id);
    }
  }

  ngOnDestroy(): void {
    this.observer.disconnect();
    this.debounceTimers.forEach(clearTimeout);
    this.videoListeners.forEach(({ element, handler, event }) => {
      element.removeEventListener(event, handler);
    });
    this.videoListeners.clear();
  }

  reset(): void {
    this.trackedIds.clear();
    this.debounceTimers.forEach(clearTimeout);
    this.debounceTimers.clear();
    this.videoListeners.forEach(({ element, handler, event }) => {
      element.removeEventListener(event, handler);
    });
    this.videoListeners.clear();
    this.observer.disconnect();
    this.initObserver();
  }
}